"use client";

import useStores from "@/lib/hooks/useStores";
import { getProductSample, getSampleQuantity } from "@/lib/api/shop/products";
import React, { useMemo, useState, useEffect } from "react";
import { getListStoreWithProductVariant } from "@/lib/api/shop/inventories";

import { InventoryStoresJson } from "@/types/Inventories.type";
import { ProductJson, ProductSample } from "@/types/Product.type";

import {
	EmptyIcon,
	LocationIcon,
	PhoneOutlineIcon,
	ZaloOutlineIcon,
} from "@/components/icons";
import LinkElement from "@/components/LinkElement";
import StoresItem, { Sample } from "~components/stores/StoresItem";
import LoadingIcon from "@/components/icons/LoadingIcon";
import PhoneFilledIcon from "@/components/icons/PhoneFilledIcon";
import useTranslationApi from "@/lib/hooks/useTranslationApi";
import { useQuery } from "@tanstack/react-query";
import { StoreJson } from "@/types/Store.type";
import { cn } from "@/utils/utils";
import LineLoading from "@/components/loadings/LineLoading";
import MessageRoundedIcon from "@/components/icons/MessageRoundedIcon";
import LoadingElement from "@/components/LoadingElement";
import Skeleton from "@/components/Skeleton";

function ProductModalAddress({ product }: { product: ProductJson }) {
	const { t } = useTranslationApi(["hotline_time"]);
	// const [sample, setSample] = useState<ProductSample>({});

	const { data: store, isLoading: isLoadingStore } = useStores({
		show_web: 1,
		sort_type: "ASC",
	});
	const listStore = store?.items || [];

	const [sample, setSample] = useState<Sample[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingSample, setIsLoadingSample] = useState<boolean>(false);
	const [listStoreStocking, setListStoreStocking] = useState<
		InventoryStoresJson[]
	>([]);

	const getListStoreStocking = async () => {
		setIsLoading(true);
		if (!product.id) return;

		try {
			const res = await getListStoreWithProductVariant({
				product_id: product.id,
				sort_by: "quantity,warehouse",
				sort_type: "desc,asc",
			});
			setListStoreStocking(res.data.items);
		} catch (error) {
			setListStoreStocking([]);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (listStore.length) {
			getSample();
		}
	}, [JSON.stringify(listStore)]);

	// async function getSample() {
	// 	setIsLoadingSample(true);
	// 	try {
	// 		const { data } = await getProductSample({
	// 			type: product.sku,
	// 		});
	// 		setSample(data);
	// 	} catch (error) {
	// 		setSample({});
	// 	} finally {
	// 		setIsLoadingSample(false);
	// 	}
	// }

	const getSample = async () => {
		try {
			setIsLoadingSample(true);
			const res = await getSampleQuantity(product.id);

			setSample(res.data);
		} catch (error) {
		} finally {
			setIsLoadingSample(false);
		}
	};

	const datas: Array<StoreStockItem> = useMemo(() => {
		let result: StoreStockItem[] = [];

		result = listStore.reduce((curr: StoreStockItem[], prev: StoreJson) => {
			const stockStoreOnProduct = product.quantities.find(
				(i) => i.store_id === prev.id
			);

			if (stockStoreOnProduct) {
				// return {};
				curr.push({
					store: prev,
					isSample: sample.find((i) => i.store_id == prev.id)?.onhand ?? false,
					isStocking: stockStoreOnProduct.actual_quantity > 0,
				});
			} else {
				curr.push({
					store: prev,
					isSample: sample.find((i) => i.store_id === prev.id)?.onhand ?? false,
					isStocking: false,
				});
			}
			return curr;
		}, []);

		return result.sort((a, b) => (a.isStocking ? -1 : 1));
	}, [listStore, sample, JSON.stringify(product)]);

	if (isLoading) {
		return (
			<div className="h-full w-full flex justify-center items-center">
				<LoadingIcon />
			</div>
		);
	}

	return (
		<div className="w-full h-full  md:pt-3 flex flex-col relative pb-14 md:pb-0">
			<div className="flex-1 overflow-y-auto scrollbarCustomer pb-4">
				<LoadingElement
					loading={isLoadingStore}
					fallbackProps={{ className: "flex-1 w-full h-full" }}
					fallback={
						<div className="w-full h-full flex items-center justify-center">
							<LoadingIcon></LoadingIcon>
							<span>Vui lòng chờ...</span>
						</div>
					}>
					{datas.length ? (
						<ul className="px-2 md:px-6 relative">
							{datas.map((data) => {
								if (!store) return <></>;
								return (
									<StoreStockItemRender
										data={data}
										isLoading={{ sample: isLoadingSample }}
										key={data.store.id}
									/>
								);
							})}
						</ul>
					) : (
						<div
							className={
								"h-full flex flex-col items-center justify-center gap-2 py-6"
							}>
							<EmptyIcon className={`text-gray-400 w-12 h-12 min-w-12`} />
							<p className={`text-left text-gray-400 text-base font-medium`}>
								Hiện chưa có store nào
							</p>
						</div>
					)}
				</LoadingElement>
			</div>
			<div className="px-6 pb-3 md:pb-4 pt-4 md:pt-7 bg-white  ">
				<LinkElement
					href="tel:19000129"
					className="px-8 py-2 border border-red-500 rounded flex justify-center items-center gap-2 text-red-500  text-base">
					<PhoneFilledIcon className=" text-red-500" />
					{t("hotline_time")}
				</LinkElement>
			</div>
		</div>
	);
}

type StoreStockItem = {
	store: StoreJson;
	isStocking: boolean;
	isSample: boolean;
};

type Loadings = {
	sample: boolean;
};

const translate = {
	product_store_near_you: "product_store_near_you",
	product_view_store: "product_view_store",
	product_stores_namperfume: "product_stores_namperfume",
	product_out_of_stock: "product_out_of_stock",
	product_not_out_of_stock: "product_not_out_of_stock",
	product_call_now: "product_call_now",
	product: "product",
	product_has_sample: "product_has_sample",
	product_has_no_sample: "product_has_no_sample",
	product_in_store: "product_in_store",
	feedback: "feedback",
};
export default ProductModalAddress;

const StoreStockItemRender = ({
	className,
	data,
	isLoading,
}: {
	className?: string;
	data: StoreStockItem;
	isLoading: Loadings;
}) => {
	const { t } = useTranslationApi(Object.values(translate));
	const store = data.store;
	const isStocking = data.isStocking;
	const isSample = data.isSample;

	return (
		<li
			className={cn(
				"flex gap-2 py-4 border-b last:border-b-0 border-gray-200 hover:border-gray-300 transition-all",
				className
			)}>
			<div className="w-10 h-10 rounded-full flex items-center justify-center border border-gray-300">
				<LocationIcon />
			</div>
			<div className="flex-1 flex flex-col gap-1 leading-[1.6]  text-sm">
				<p className=" font-bold">{data.store.name}</p>

				<>
					{isStocking ? (
						<div>
							<p className="text-blue-500 font-bold uppercase">
								{/* Còn hàng */}
								{t(translate.product_not_out_of_stock)}
							</p>
						</div>
					) : (
						<p className="font-bold uppercase text-red-500">
							{t(translate.product_out_of_stock)}
						</p>
					)}
				</>
				<>
					<LoadingElement
						loading={isLoading.sample}
						fallback={
							<div>
								<Skeleton className="h-2 rounded-sm bg-gray-300"></Skeleton>
							</div>
						}>
						{isSample && (
							<div>
								{isSample ? (
									<p className="capitalize">
										{t(translate.product)}{" "}
										<b>{t(translate.product_has_sample)}</b>{" "}
										{t(translate.product_in_store)}
									</p>
								) : null}
							</div>
						)}
					</LoadingElement>
				</>
				<p className="  text-gray-500">{store.address}</p>
				<div className="flex items-center divide-x divide-gray-500 gap-4 py-1">
					<LinkElement
						href={`tel:${store.phone}`}
						className="flex items-center gap-1 group">
						<PhoneOutlineIcon className="text-gray-500 w-4 h-4 min-w-4 group-hover:text-red-500 transition-all" />
						<span className="group-hover:text-red-500 text-gray-500 text-sm underline underline-offset-[3px] transition-all">
							{t(translate.product_call_now)}
						</span>
					</LinkElement>
					<LinkElement
						href={`https://zalo.me/${store.phone}`}
						target="_blank"
						className="flex items-center gap-1 group pl-4">
						<ZaloOutlineIcon className="text-gray-500 w-4 h-4 min-w-4 group-hover:text-blue-500 transition-all" />
						<span className="group-hover:text-blue-500 text-gray-500 text-sm underline underline-offset-[3px] transition-all">
							Zalo
						</span>
					</LinkElement>

					<LinkElement
						href={`/pages/feedback/${store.code}`}
						className="flex items-center gap-1 group pl-4">
						<MessageRoundedIcon className="text-gray-500 w-4 h-4 min-w-4 group-hover:text-blue-500 transition-all" />
						<span className="group-hover:text-blue-500 text-gray-500 text-sm underline underline-offset-[3px] transition-all">
							{t(translate.feedback)}
						</span>
					</LinkElement>
				</div>
			</div>
		</li>
	);
};
